import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import PrimaryButton from '../buttons/primaryButton';
import MultiplePlayerDisplay from '../playerDisplay/multiple';
import { publicStartGame, startGame } from '../../../features/game/gameSlice';

import { useAuth } from '../../../contexts/authContext';
import { Roles, hasWorkspaceAccess } from '../../../helpers/access';
import { getEventScoreMethod } from '../../../features/event/eventSlice';

const GameNotStartedCard = ({ game, actions, isPublic, portal }) => {
  const dispatch = useDispatch();
  const { currentUser, currentUserProfile, currentWorkspace } = useAuth();
  const [openGreenRinkDialog, setOpenGreenRinkDialog] = useState(false);
  const [selectedGreen, setSelectedGreen] = useState('');
  const [selectedRink, setSelectedRink] = useState('');
  const [greens, setGreens] = useState([]);
  const [rinks, setRinks] = useState([]);
  const [isPublicStart, setIsPublicStart] = useState(false);
  const [isPortalStart, setIsPortalStart] = useState(false);

  const event = useSelector((state) => state.event.event);
  const activeEvent = portal?.event || event;

  const gameScorer = game.scorer?.id || game.scorer;

  // Extract available greens and rinks from event based on game venue
  useEffect(() => {
    if (activeEvent && game.venue) {
      const venueId = typeof game.venue === 'string' ? game.venue : game.venue.id;

      // Check if event.greens exists and is an array
      if (Array.isArray(activeEvent.greens) && activeEvent.greens.length > 0) {
        console.log('Event greens:', activeEvent.greens);
        const venue = activeEvent.greens.find((v) => v.id === venueId);
        console.log('Found venue:', venue);

        if (venue && Array.isArray(venue.greens) && venue.greens.length > 0) {
          const greenList = venue.greens.map((green) => green.name);
          console.log('Green list:', greenList);
          setGreens(greenList.sort());
        } else {
          console.log('No greens found in venue or venue not found');
          setGreens([]);
        }
      } else {
        console.log('No greens array in event');
        setGreens([]);
      }
    }
  }, [activeEvent, game.venue]);

  // Update rinks based on selected green
  useEffect(() => {
    if (activeEvent && selectedGreen && game.venue) {
      const venueId = typeof game.venue === 'string' ? game.venue : game.venue.id;
      const venue = activeEvent.greens.find((v) => v.id === venueId);

      if (venue && venue.greens) {
        const green = venue.greens.find((g) => g.name === selectedGreen);
        if (green && green.rinks) {
          const rinkList = green.rinks
            .filter((rink) => rink.active)
            .map((rink) => rink.rink)
            .sort((a, b) => a - b);
          setRinks(rinkList);
          setSelectedRink('');
        }
      }
    }
  }, [selectedGreen, activeEvent, game.venue]);

  useEffect(() => {
    if (portal) return;
    if (!event?.scoringMethod && isPublic && game.event) {
      dispatch(getEventScoreMethod(game.event));
    }
  }, [event, dispatch, isPublic, game.event, portal]);

  const handleStartGame = (mode = 'auth') => {
    const publicMode = mode === 'public';
    setIsPublicStart(publicMode);
    setIsPortalStart(mode === 'portal');

    if (mode === 'portal') {
      // The portal owns the start call, but the card still asks for a green
      // and rink when the draw has not placed this game yet.
      if (game.green && game.rink) {
        portal.startGame({});
      } else {
        setOpenGreenRinkDialog(true);
      }
      return;
    }

    // Standalone quick games have no venue/green/rink - start directly.
    if (publicMode && !game.event) {
      dispatch(publicStartGame({ gameId: game.id }));
      return;
    }

    // If game already has green and rink, start directly
    if (game.green && game.rink) {
      if (publicMode) {
        dispatch(publicStartGame({ gameId: game.id }));
      } else {
        dispatch(startGame({ gameId: game.id }));
      }
    } else {
      // Otherwise, open dialog to select green/rink
      setOpenGreenRinkDialog(true);
    }
  };

  const handleConfirmGreenRink = () => {
    if (selectedGreen && selectedRink) {
      if (isPortalStart) {
        portal.startGame({
          green: selectedGreen,
          rink: parseInt(selectedRink, 10)
        });
      } else if (isPublicStart) {
        dispatch(
          publicStartGame({
            gameId: game.id,
            green: selectedGreen,
            rink: parseInt(selectedRink, 10)
          })
        );
      } else {
        dispatch(
          startGame({ gameId: game.id, green: selectedGreen, rink: parseInt(selectedRink, 10) })
        );
      }
      setOpenGreenRinkDialog(false);
      setSelectedGreen('');
      setSelectedRink('');
      setIsPublicStart(false);
      setIsPortalStart(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenGreenRinkDialog(false);
    setSelectedGreen('');
    setSelectedRink('');
    setIsPublicStart(false);
    setIsPortalStart(false);
  };

  return (
    <Stack justifyContent={'space-between'} height="100%" mt={2}>
      <MultiplePlayerDisplay teams={game.teams} />

      <Box
        display={'flex'}
        flexDirection={'row'}
        alignSelf={'center'}
        justifySelf={'flex-end'}
        mt={2}>
        {portal ? (
          <PrimaryButton
            color="success"
            variant="contained"
            disabled={portal.busy}
            onClick={() => handleStartGame('portal')}>
            Start Game
          </PrimaryButton>
        ) : (
          <>
            {(hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.SCORER) ||
              (currentUser && currentUser.uid === gameScorer)) && (
              <PrimaryButton color="success" onClick={() => handleStartGame('auth')}>
                Start Game
              </PrimaryButton>
            )}

            {isPublic && (
              <PrimaryButton
                color="success"
                variant="contained"
                onClick={() => handleStartGame('public')}>
                Start Game
              </PrimaryButton>
            )}
          </>
        )}
      </Box>

      {/* Green/Rink Selection Dialog */}
      <Dialog open={openGreenRinkDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Select Green and Rink</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Green</InputLabel>
              <Select
                value={selectedGreen}
                label="Green"
                onChange={(e) => setSelectedGreen(e.target.value)}>
                {greens.map((green) => (
                  <MenuItem key={green} value={green}>
                    {green}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!selectedGreen}>
              <InputLabel>Rink</InputLabel>
              <Select
                value={selectedRink}
                label="Rink"
                onChange={(e) => setSelectedRink(e.target.value)}>
                {rinks.map((rink) => (
                  <MenuItem key={rink} value={rink}>
                    {rink}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmGreenRink}
            variant="contained"
            color="primary"
            disabled={!selectedGreen || !selectedRink}>
            Start Game
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default GameNotStartedCard;
